import * as React from "react";

type FocusHandler = (e: Event) => void;

// The last element focused outside any dialog. A field with autoFocus inside a dialog takes focus before
// Radix's open event fires, so document.activeElement alone can't say where the person came from.
let lastOutside: HTMLElement | null = null;
if (typeof document !== "undefined") {
  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target;
      if (t instanceof HTMLElement && t !== document.body && !t.closest('[role="dialog"]')) lastOutside = t;
    },
    true,
  );
}

/** Our dialogs open from state, not a Radix Trigger, so Radix has nowhere to send focus when they close
 *  and it lands on <body>. This remembers what had focus when the dialog opened and returns there. */
export function useReturnFocus(onOpenAutoFocus?: FocusHandler, onCloseAutoFocus?: FocusHandler) {
  const returnTo = React.useRef<HTMLElement | null>(null);
  return {
    onOpenAutoFocus: (e: Event) => {
      const el = document.activeElement;
      const content = e.target instanceof HTMLElement ? e.target : null;
      const fromOutside = el instanceof HTMLElement && el !== document.body && !content?.contains(el) && !el.closest('[role="dialog"]');
      returnTo.current = fromOutside ? (el as HTMLElement) : lastOutside;
      onOpenAutoFocus?.(e);
    },
    onCloseAutoFocus: (e: Event) => {
      onCloseAutoFocus?.(e);
      if (e.defaultPrevented) return;
      // Radix skips the open event when a field inside already has focus (autoFocus), so fall back.
      const el = returnTo.current ?? lastOutside;
      returnTo.current = null;
      if (el && el.isConnected) {
        e.preventDefault();
        // A button that is busy (disabled while its request runs) can't take focus yet: try again shortly.
        const go = () => el.isConnected && el.focus({ preventScroll: true });
        if ((el as HTMLButtonElement).disabled) setTimeout(go, 150);
        else go();
      }
    },
  };
}
